import React from "react";
import { Download, X } from "lucide-react";

/**
 * Live status card shown while a bulk ZIP is being prepared and downloaded.
 *
 * Props:
 *  - stage: "preparing" | "downloading" | "done"
 *  - progress: 0-100 (real download-progress % once bytes start arriving)
 *  - fileCount: total media files in the event (photos + videos)
 *  - elapsedSec: seconds since the request started
 *  - estimatedSec: rough total estimate to render the fake-progress bar
 *  - onCancel: optional close/hide handler
 */
export default function ZipProgressCard({ stage, progress, fileCount, elapsedSec, estimatedSec, onCancel }) {
  // While the server is still building (no bytes yet), show an eased fake
  // progress capped at 90 % so the user perceives motion. As soon as real
  // download bytes arrive, we switch to the real progress %.
  const fakePreparePct = Math.min(90, Math.round((elapsedSec / Math.max(1, estimatedSec)) * 90));
  const displayPct = stage === "downloading" ? Math.max(progress, fakePreparePct) : fakePreparePct;
  const done = stage === "done";
  const label = done
    ? "Download ready — check your Downloads folder."
    : stage === "downloading"
      ? `Downloading… ${progress}%`
      : `Bundling ${fileCount} file${fileCount === 1 ? "" : "s"} into a ZIP…`;

  return (
    <div
      data-testid="zip-progress-card"
      className="mb-6 bg-white border border-navy-700/15 rounded-2xl p-5 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-navy-700/8 flex items-center justify-center">
            <Download className={`w-5 h-5 text-navy-700 ${done ? "" : "animate-bounce"}`} />
          </div>
          <div>
            <div className="text-navy-700 font-semibold text-sm">{label}</div>
            <div className="text-xs text-navy-700/60 mt-0.5">
              {done
                ? "You can close this card."
                : `Elapsed ${elapsedSec}s${estimatedSec ? ` · Est. ${estimatedSec}s total` : ""}`}
            </div>
          </div>
        </div>
        {done && onCancel && (
          <button
            onClick={onCancel}
            aria-label="Close"
            data-testid="zip-progress-close-btn"
            className="text-navy-700/40 hover:text-navy-700"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="mt-4 h-2 rounded-full bg-cream-200 overflow-hidden">
        <div
          data-testid="zip-progress-bar"
          className={`h-full transition-all duration-300 ${done ? "bg-emerald-500" : "bg-coral-300"}`}
          style={{ width: `${done ? 100 : displayPct}%` }}
        />
      </div>
    </div>
  );
}
